import { createContext, useContext } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export const EASE = [0.22, 1, 0.36, 1];

// Scroll helper shared by all CTAs on the page (Lenis-aware).
export const HolidayScrollContext = createContext(() => {});
export const useHolidayScroll = () => useContext(HolidayScrollContext);

// Masked line-by-line reveal for display headings.
export function MaskedLines({ lines, className = "", delay = 0, stagger = 0.12, as: Tag = "span" }) {
  return (
    <span className={`block ${className}`}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className="block will-change-transform"
            initial={{ y: "115%", rotate: 1.5 }}
            animate={{ y: 0, rotate: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: delay + i * stagger }}
          >
            <Tag>{line}</Tag>
          </motion.span>
        </span>
      ))}
    </span>
  );
}

// Scroll-triggered reveal (once).
export function Reveal({ children, className = "", delay = 0, y = 40, as = "div", ...rest }) {
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -80px 0px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
      {...rest}
    >
      {children}
    </Comp>
  );
}

// Numbered chapter eyebrow: "02 — The packages"
export function Chapter({ number, label, tone = "light", className = "" }) {
  const color = tone === "light" ? "text-moss" : "text-bone/70";
  const line = tone === "light" ? "bg-moss" : "bg-bone/50";
  return (
    <Reveal className={`flex items-center gap-4 text-xs uppercase tracking-wider-3 ${color} ${className}`}>
      <span className="font-display text-2xl leading-none tracking-normal">{number}</span>
      <span className={`inline-block w-8 h-px ${line}`} />
      <span>{label}</span>
    </Reveal>
  );
}

// Primary / secondary CTA that scrolls to the enquiry form.
export function EnquiryButton({ variant = "dark", className = "", children, testid, target = "enquiry", pkg }) {
  const scrollTo = useHolidayScroll();
  const styles = {
    dark: "bg-forest text-bone hover:bg-forest-deep",
    light: "bg-bone text-forest hover:bg-bone-deep",
    outlineLight: "border border-bone/60 text-bone hover:bg-bone hover:text-forest",
    outlineDark: "border border-forest text-forest hover:bg-forest hover:text-bone",
    terracotta: "bg-terracotta text-white hover:opacity-90",
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => scrollTo(target, pkg)}
      className={`btn-lift inline-flex items-center justify-center gap-3 px-8 py-4 min-h-[52px] text-xs uppercase tracking-wider-3 ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

// Slow editorial marquee.
export function Marquee() {
  const { t } = useTranslation();
  const words = t("holidays.marquee", { returnObjects: true });
  const row = [...words, ...words];
  return (
    <div className="relative overflow-hidden border-y border-bone-deep bg-bone py-6" aria-hidden="true" data-testid="holidays-marquee">
      <div className="flex whitespace-nowrap hol-marquee w-max">
        {[0, 1].map((k) => (
          <div key={k} className="flex items-center">
            {row.map((w, i) => (
              <span key={`${k}-${i}`} className="flex items-center font-display text-3xl md:text-5xl text-forest/90 italic pr-10 md:pr-16">
                {w}
                <span className="ml-10 md:ml-16 inline-block w-1.5 h-1.5 bg-terracotta rounded-full" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
